import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Icon,
  ModalIsland,
  type NestedListRowData,
  NestedListRowGroup,
  Typography,
} from '@saviynt/design-system';

import type { FilterOption, FilterPill } from './FilterPills';

import { type RuleAction, type User } from '../../../services';

import { type ActiveFilters } from './helpers/filterUtils';
import {
  createLoadingStateContent,
  createParentCenterContent,
  createParentLeadingContent,
  createParentTrailingContent,
  createPlaceholderContent,
  createUserCenterContent,
} from './helpers/rowContentHelpers';
import { generateFilterPillsFromEntitlementRows } from './helpers/ruleDataFilterUtils';
import { useEntitlementDeletion } from './hooks/use-entitlementdeletion';
import { useEntitlementExpansion } from './hooks/use-entitlementexpansion';
import { useSidePanelState } from './hooks/use-sidepanelstate';
import { CONSTANTS } from './constants';
import EntitlementsToolbar from './EntitlementsToolbar';
import RuleSidePanel from './RuleSidePanel';

import './IncludedEntitlements.css';

interface IncludedEntitlementsProps {
  actions: RuleAction[];
  ruleData:
    | {
        userMatchCount: number;
        confidence: number;
        entitlements: number;
        conditions?: {
          objectName: string;
          operation: string;
          objectValue: string | string[];
        }[];
      }
    | undefined;
  /**
   * Callback fired when rule data changes (e.g., entitlement groups deleted)
   * Provides current rule actions excluding any deleted entitlement groups
   */
  onRuleDataChange?: (actions: RuleAction[]) => void;
}

export interface GroupedEntitlement {
  endpointName: string;
  endpointDisplayName: string | null;
  endpointDescription: string; // from API: entitlementMetaData.endpoint.description
  logo: string;
  confidence: number;
  totalUserCount: number; // total users who match rule conditions
  entitlements: {
    evKey: number;
    entitlementValueName: string;
    evDisplayName: string | null;
    evDescription: string | null;
    entitlementTypeName: string;
    entitlementDisplayName: string; // from API: entitlementMetaData.entitlementType.entitlementDisplayName
    entitlementTypeDescription: string; // from API: entitlementMetaData.entitlementType.description
    existingUserCount: number; // from API: entitlementMetaData.existingUserCount
    trustScore: number;
  }[];
  users: User[]; // users who have access to this entitlement group
  isLoadingUsers: boolean;
  // Pagination fields for load more functionality
  currentPage: number;
  hasMoreUsers: boolean | null;
  isLoadingMoreUsers: boolean;

  // Additional fields for entitlement side panel display only
  entitlementGlossary?: string; // from API: entitlementMetaData.entitlementGlossary (not yet available)
  entitlementPrivileged?: string; // from API: entitlementMetaData.entitlementPrivileged (not yet available)
  securitySystemName?: string; // from API: entitlementMetaData.securitySystem.securitySystemName
  securitySystemDisplayName?: string; // from API: entitlementMetaData.securitySystem.securitySystemDisplayName
}

export interface EntitlementRow {
  // Endpoint fields (flattened from GroupedEntitlement)
  endpointName: string;
  endpointDisplayName: string | null;
  endpointDescription: string;
  logo: string;
  // Entitlement fields (flattened from entitlements[0])
  evKey: number;
  entitlementValueName: string;
  evDisplayName: string | null;
  evDescription: string | null;
  entitlementTypeName: string;
  entitlementDisplayName: string;
  entitlementTypeDescription: string;
  existingUserCount: number;
  trustScore: number;
  // Computed fields
  confidence: number;
  totalUserCount: number;
  users: User[];
  isLoadingUsers: boolean;
  currentPage: number;
  hasMoreUsers: boolean | null;
  isLoadingMoreUsers: boolean;

  // Additional fields for entitlement side panel display (flattened from GroupedEntitlement)
  entitlementGlossary?: string;
  entitlementPrivileged?: string;
  securitySystemName?: string;
  securitySystemDisplayName?: string;
}

export const transformToEntitlementRows = (
  groupedEntitlements: GroupedEntitlement[]
): EntitlementRow[] =>
  groupedEntitlements.flatMap((group) =>
    group.entitlements.map((entitlement) => ({
      // Endpoint fields
      endpointName: group.endpointName,
      endpointDisplayName: group.endpointDisplayName,
      endpointDescription: group.endpointDescription,
      logo: group.logo,
      // Entitlement fields
      evKey: entitlement.evKey,
      entitlementValueName: entitlement.entitlementValueName,
      evDisplayName: entitlement.evDisplayName,
      evDescription: entitlement.evDescription,
      entitlementTypeName: entitlement.entitlementTypeName,
      entitlementDisplayName: entitlement.entitlementDisplayName,
      entitlementTypeDescription: entitlement.entitlementTypeDescription,
      existingUserCount: entitlement.existingUserCount,
      trustScore: entitlement.trustScore,
      // Computed fields
      confidence: group.confidence,
      totalUserCount: group.totalUserCount,
      users: group.users,
      isLoadingUsers: group.isLoadingUsers,
      currentPage: group.currentPage,
      hasMoreUsers: group.hasMoreUsers,
      isLoadingMoreUsers: group.isLoadingMoreUsers,

      // Additional fields for entitlement side panel display
      entitlementGlossary: group.entitlementGlossary,
      entitlementPrivileged: group.entitlementPrivileged,
      securitySystemName: group.securitySystemName,
      securitySystemDisplayName: group.securitySystemDisplayName,
    }))
  );

function IncludedEntitlements({
  actions = [],
  ruleData = undefined,
  onRuleDataChange, // Callback to notify parent when rule data changes (deletions, etc.)
}: IncludedEntitlementsProps): JSX.Element {
  // Search and filter state - these remain in main component
  const [searchValue, setSearchValue] = useState('');
  const [filterPills, setFilterPills] = useState<FilterPill[]>([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    applications: [],
    entitlements: [],
    access_status: [], // Start with no filter - show all entitlements
  });
  // Track deleted entitlement groups
  const [deletedEntGroups, setDeletedEntGroups] = useState<Set<string>>(
    new Set()
  );

  // Use entitlement expansion hook for all expansion-related logic
  const {
    groupedEntitlements,
    expandedEntitlements,
    handleExpandToggle,
    loadMoreUsers,
    clearAllUserData,
    handleCollapseAllGroups,
    syncExpandedEntitlementsWithRowsData,
  } = useEntitlementExpansion({
    actions,
    ruleData,
    activeFilters,
  });

  // Transform to individual entitlement rows
  const entitlementRows = useMemo(
    () => transformToEntitlementRows(groupedEntitlements),
    [groupedEntitlements]
  );

  // Computed active entitlements (excludes deleted ones)
  const activeEntitlementRows = useMemo(
    () =>
      entitlementRows.filter(
        (row) => !deletedEntGroups.has(row.evKey.toString())
      ),
    [entitlementRows, deletedEntGroups]
  );

  // Generate current rule actions (excludes deleted entitlement rows)
  // This creates RuleAction objects from activeEntitlementRows for:
  // - ActivateRuleModal (API payload)
  // - AppAccessTiles (app tiles with correct counts)
  const getCurrentRuleActions = useCallback(
    (): RuleAction[] =>
      activeEntitlementRows.map((row) => ({
        entitlementMetaData: {
          evKey: row.evKey,
          entitlementValueName: row.entitlementValueName,
          evDisplayName: row.evDisplayName ?? '',
          evDescription: row.evDescription,
          existingUserCount: row.existingUserCount,
          trustScore: row.trustScore,
          endpoint: {
            endpointKey: 0, // We don't have this in EntitlementRow, using 0 as placeholder
            endpointName: row.endpointName,
            endpointDisplayName: row.endpointDisplayName,
            description: row.endpointDescription,
            logo: row.logo,
          },
          entitlementType: {
            entitlementTypeKey: 0, // We don't have this in EntitlementRow, using 0 as placeholder
            entitlementTypeName: row.entitlementTypeName,
            entitlementDisplayName: row.entitlementDisplayName,
            description: row.entitlementTypeDescription,
          },
        },
      })),
    [activeEntitlementRows]
  );

  useEffect(() => {
    // Notify parent component when rule data changes
    // This ensures AppAccessTiles, ActivateRuleModal, and other consumers
    // always receive the current rule state (excluding deleted entitlement groups)
    if (onRuleDataChange) {
      const currentActions = getCurrentRuleActions();

      onRuleDataChange(currentActions);
    }
  }, [getCurrentRuleActions, onRuleDataChange]);

  // State for NestedListRowGroup
  const [rowsData, setRowsData] = useState<NestedListRowData[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<
    EntitlementRow | User | null
  >(null);

  const handleSetSelectedRowData = (data: EntitlementRow | User | null) => {
    setSelectedRowData(data);
  };

  // Sidepanel state management
  const {
    isSidePanelOpen,
    sidePanelData,
    sidePanelType,
    handleCloseSidePanel,
  } = useSidePanelState(selectedRowData, setSelectedRowData);

  // Entitlement deletion functionality
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const {
    entGroupBeingDeleted,
    isDeleteEntGroupModalOpen,
    handleDeleteEntRowClick,
    handleConfirmEntRowDelete,
    handleCancelDelete,
  } = useEntitlementDeletion({
    selectedRowData,
    setSelectedRowData,
    handleCloseSidePanel,
    setDeletedEntGroups,
  });

  // Generate filter pills from active entitlements (excludes deleted ones)
  useEffect(() => {
    if (activeEntitlementRows.length > 0) {
      const generatedPills = generateFilterPillsFromEntitlementRows(
        activeEntitlementRows
      );

      setFilterPills(generatedPills);

      // Clean up active filters - remove selections that no longer exist
      const availableApplications = new Set(
        activeEntitlementRows.map(
          (row) => row.endpointDisplayName ?? row.endpointName
        )
      );
      const availableEntitlements = new Set(
        activeEntitlementRows.map((row) => String(row.evKey))
      );

      setActiveFilters((prev) => ({
        ...prev,
        applications: prev.applications.filter((app) =>
          availableApplications.has(app)
        ),
        entitlements: prev.entitlements.filter((ent) =>
          availableEntitlements.has(ent)
        ),
        // access_status: Don't clean up - Present/Absent are always valid
      }));
    }
  }, [activeEntitlementRows]);

  // Apply filters and search to create filtered entitlements using rule data
  const filteredEntitlementRows = useMemo(() => {
    let result = activeEntitlementRows; // Already filtered by evKey deletion

    // Apply application filter
    if (activeFilters.applications.length > 0) {
      result = result.filter((row) => {
        const applicationDisplayName =
          row.endpointDisplayName ?? row.endpointName;

        return activeFilters.applications.includes(applicationDisplayName);
      });
    }

    // Apply entitlement filter
    if (activeFilters.entitlements.length > 0) {
      result = result.filter((row) =>
        activeFilters.entitlements.includes(String(row.evKey))
      );
    }

    // Apply search
    if (searchValue.trim()) {
      const searchTerm = searchValue.toLowerCase();

      // search with null checks / fallbacks
      result = result.filter(
        (row) =>
          (row.evDisplayName ?? row.entitlementValueName)
            .toLowerCase()
            .includes(searchTerm) ||
          (row.endpointDisplayName ?? row.endpointName)
            .toLowerCase()
            .includes(searchTerm)
      );
    }

    return result;
  }, [activeEntitlementRows, activeFilters, searchValue]);

  // Toolbar event handlers
  const handleFilterClick = () => {
    // No API call needed - filter pills are already generated from rule data
  };

  const handleSearchChange = (newSearchValue: string) => {
    setSearchValue(newSearchValue);
    handleCloseSidePanel(); // Close side panel on search
  };

  const handleRowsClick = () => {
    handleCollapseAllGroups();
  };

  const handleColumnsClick = () => {
    // TODO: future dropdown implementation
  };

  const handleFilterChange = (
    filterId: string,
    selectedOptions: FilterOption[]
  ) => {
    // Special handling for access_status filter - collapse all rows and clear user data
    if (filterId === 'access_status') {
      handleCollapseAllGroups();
      clearAllUserData(); // Clear cached user data to force fresh API calls
    }

    // Update active filters state
    setActiveFilters((prev) => ({
      ...prev,
      [filterId]: selectedOptions.map((option) => option.value),
    }));

    handleCloseSidePanel(); // Close side panel on filter change
  };

  const handleClearAllFilters = useCallback(() => {
    // Special handling - collapse all rows and clear user data (like access_status filter change)
    handleCollapseAllGroups();
    clearAllUserData();

    // Clear all filters
    setActiveFilters({
      applications: [],
      entitlements: [],
      access_status: [],
    });

    handleCloseSidePanel(); // Close side panel on clear filters
  }, [handleCollapseAllGroups, clearAllUserData, handleCloseSidePanel]);

  // Check if any filters are active for badge display
  const hasActiveFilters = useMemo(
    () =>
      activeFilters.applications.length > 0 ||
      activeFilters.entitlements.length > 0 ||
      activeFilters.access_status.length > 0,
    [activeFilters]
  );

  // Update rowsData when filteredEntitlementRows changes
  useEffect(() => {
    const newRowsData: NestedListRowData[] = filteredEntitlementRows.map(
      (row: EntitlementRow) => ({
        id: row.evKey.toString(),
        data: row, // store the full row data
        afterContent: (() =>
          row.hasMoreUsers && !row.isLoadingUsers ? (
            <Button
              kind='outlined'
              size='small'
              onClick={() => loadMoreUsers(row.endpointName)}
              isDisabled={row.isLoadingMoreUsers}
              isLoading={row.isLoadingMoreUsers}
              className='IncludedEntitlements-loadMoreButton'>
              Load more
            </Button>
          ) : null)(),
        isCheckboxDisabled: true,
        isNestedRowExpanded: (() => {
          const isExpanded = expandedEntitlements.has(row.evKey.toString());

          return isExpanded;
        })(), // Set based on our expansion tracking
        // No manual chevron - NestedListRowGroup adds it automatically when childRows exist
        leadingComp: createParentLeadingContent(),
        centerContent: createParentCenterContent(row, handleSetSelectedRowData),
        trailingContent: createParentTrailingContent(
          row,
          filteredEntitlementRows.length,
          handleDeleteEntRowClick
        ),
        // Custom handler for when row is expanded
        handleExpandToggler: handleExpandToggle,
        // Always provide childRows to show chevron (even if empty initially)
        childRows: ((): NestedListRowData[] => {
          if (row.isLoadingUsers) {
            return [
              {
                id: `${row.evKey}-loading`,
                data: {},
                isCheckboxDisabled: true,
                centerContent: createLoadingStateContent(),
              },
            ];
          }

          if (row.users.length > 0) {
            return row.users.map((user: User) => ({
              id: `${row.evKey}-user-${user.userkey}`,
              data: user,
              isCheckboxDisabled: true,
              centerContent: createUserCenterContent(
                user,
                handleSetSelectedRowData
              ),
            }));
          }

          // Always return at least one placeholder to ensure chevron shows
          return [
            {
              id: `${row.evKey}-placeholder`,
              data: {},
              isCheckboxDisabled: true,
              centerContent: createPlaceholderContent(),
            },
          ] as NestedListRowData[];
        })(),
      })
    );

    setRowsData(newRowsData);
  }, [
    filteredEntitlementRows,
    handleExpandToggle,
    expandedEntitlements,
    loadMoreUsers,
    searchValue,
    handleDeleteEntRowClick,
  ]);

  // State Reconciliation: Call the hook's sync function when rowsData changes
  // This ensures our expansion state stays in sync with the DS component's actual state
  useEffect(() => {
    syncExpandedEntitlementsWithRowsData(rowsData);
  }, [rowsData, syncExpandedEntitlementsWithRowsData]);

  if (!ruleData || actions.length === 0) {
    return (
      <Box className='IncludedEntitlements'>
        <Typography
          kind='h3'
          color='--color-foreground-secondary-bold'
          className='IncludedEntitlements-title'>
          Included Entitlements
        </Typography>
        <Typography kind='body2' className='IncludedEntitlements-emptyState'>
          No entitlements found for this rule.
        </Typography>
      </Box>
    );
  }

  return (
    <Box tag='section' className='IncludedEntitlements'>
      <Typography
        kind='h3'
        color='--color-foreground-secondary-bold'
        className='IncludedEntitlements-title'>
        Included Entitlements
      </Typography>
      <EntitlementsToolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onFilterClick={handleFilterClick}
        onRowsClick={handleRowsClick}
        onColumnsClick={handleColumnsClick}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        hasActiveFilters={hasActiveFilters}
        filterPills={filterPills}
        activeFilters={activeFilters}
      />

      {/* No results message */}
      {filteredEntitlementRows.length === 0 && (
        <Box className='IncludedEntitlements-noResults'>
          <Icon
            kind='AlertCriticalOutline'
            size='medium'
            color='--color-foreground-neutral-bold'
          />
          <Typography kind='h3' color='--color-foreground-neutral-bold'>
            No entitlements match your search or filter criteria.
          </Typography>
        </Box>
      )}

      <NestedListRowGroup
        rowsData={rowsData}
        setRowsData={setRowsData}
        selectedRowData={selectedRowData}
        setSelectedRowData={handleSetSelectedRowData}
        tallestRowHeight={CONSTANTS.TALLEST_ROW_HEIGHT}
        afterContentHeight={CONSTANTS.AFTER_CONTENT_HEIGHT}
        className='IncludedEntitlements-nestedListRowGroup'
      />

      <RuleSidePanel
        key={(() => {
          if (!sidePanelData) {
            return 'none';
          }

          if (sidePanelType === 'user') {
            return `user-${(sidePanelData as User).userkey}`;
          }

          return `entitlement-${(sidePanelData as EntitlementRow).evKey}`;
        })()}
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
        data={sidePanelData}
        type={sidePanelType}
      />

      {/* entitlement group delete confirmation modal */}
      <ModalIsland
        kind='warning'
        customAlertIcon={<Icon kind='AlertWarningOutline' size='xxLarge' />}
        isOpen={isDeleteEntGroupModalOpen}
        onClose={handleCancelDelete}
        title='Confirm Removal'
        alertTitle='Remove Entitlement?'
        alertSubtitle={
          entGroupBeingDeleted
            ? `Are you sure you want to remove the ${
                entGroupBeingDeleted.evDisplayName ??
                entGroupBeingDeleted.entitlementValueName
              } entitlement?`
            : ''
        }
        primaryButton={
          <Button kind='filled' onClick={handleConfirmEntRowDelete}>
            Confirm Removal
          </Button>
        }
        classNames={{
          // Note: this custom classname is being hashed by the DS
          alertBgClassName: 'IncludedEntitlements-deleteEntModal',
        }}
      />
    </Box>
  );
}

IncludedEntitlements.defaultProps = {
  onRuleDataChange: undefined,
};

export default IncludedEntitlements;
